'use client';

import InlineStyle from '@/components/InlineStyle';
import { Rocket, Sparkles, Trophy, Users, Lightbulb, Zap, Star, ArrowRight } from 'lucide-react';

/* Content verbatim from the WP source (static-pages.json key 'l-e-a-p').
   This page is intentionally more playful than the rest of the site —
   the audience is college interns, not enterprise CX leaders. */
import { useTranslation } from "@/hooks/useTranslation";
const PERKS = [{
  Icon: Rocket,
  title: 'Dive into real projects',
  desc: 'Make your mark from Day 1.',
  bg: 'linear-gradient(135deg,#FF6B9D,#C44569)'
}, {
  Icon: Users,
  title: 'Mentors who matter',
  desc: 'Learn from pros who’ve been there, done that.',
  bg: 'linear-gradient(135deg,#4F6EF7,#6C47F5)'
}, {
  Icon: Trophy,
  title: 'Level up',
  desc: 'Upskill with certified courses fully supported by Locobuzz.',
  bg: 'linear-gradient(135deg,#10B981,#0891B2)'
}, {
  Icon: Sparkles,
  title: 'Claim your PPO',
  desc: 'Excel and earn a shot at a Pre-Placement Offer.',
  bg: 'linear-gradient(135deg,#F5A623,#E8304A)'
}];
const LEAP_LETTERS = [{
  letter: 'L',
  word: 'Learn',
  desc: 'from mentors who care, real projects that challenge, and a culture that encourages asking “why not?”',
  color: '#FF6B9D',
  emoji: '📚'
}, {
  letter: 'E',
  word: 'Explore',
  desc: 'new skills, cross-team collaborations, and the thrill of seeing your ideas come alive. You’re not bound to one role!',
  color: '#4F6EF7',
  emoji: '🔭'
}, {
  letter: 'A',
  word: 'Act',
  desc: 'with ownership, make an impact, and see the tangible results of your work. Celebrate every win and go beyond it!',
  color: '#10B981',
  emoji: '⚡'
}, {
  letter: 'P',
  word: 'Progress',
  desc: 'that beats theory. Walk away with more than a certificate, gain confidence, creativity, & a network that lasts.',
  color: '#F5A623',
  emoji: '🚀'
}];
const VIBE_BADGES = [{
  emoji: '☕',
  label: 'Free chai'
}, {
  emoji: '🎮',
  label: 'Game zone'
}, {
  emoji: '🍕',
  label: 'Friday socials'
}, {
  emoji: '🎵',
  label: 'Office playlist'
}, {
  emoji: '🐶',
  label: 'Pet-friendly desks'
}, {
  emoji: '🎉',
  label: 'Win celebrations'
}];
export default function LeapClient() {
  const {
    t
  } = useTranslation();
  return <>
      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="leap-hero">
        <div className="leap-hero__blob leap-hero__blob--1" aria-hidden="true" />
        <div className="leap-hero__blob leap-hero__blob--2" aria-hidden="true" />
        <div className="leap-hero__blob leap-hero__blob--3" aria-hidden="true" />
        {/* Floating sparkles */}
        <span className="leap-sparkle" style={{
        top: '18%',
        left: '12%',
        animationDelay: '0s'
      }} aria-hidden="true">{'✨'}</span>
        <span className="leap-sparkle" style={{
        top: '26%',
        right: '14%',
        animationDelay: '1.2s'
      }} aria-hidden="true">{'⭐'}</span>
        <span className="leap-sparkle" style={{
        bottom: '20%',
        left: '8%',
        animationDelay: '2.4s'
      }} aria-hidden="true">{'💫'}</span>
        <span className="leap-sparkle" style={{
        bottom: '14%',
        right: '18%',
        animationDelay: '0.6s'
      }} aria-hidden="true">{'🌟'}</span>

        <div className="leap-hero__inner">
          <span className="leap-badge">
            <Sparkles size={12} strokeWidth={2.4} />
            {t("l_e_a_p.internship_programme")}
          </span>
          <h1 className="leap-hero__h1">
            {t("l_e_a_p.your")} <span className="leap-hero__accent">{t("l_e_a_p.leap")}</span><br />
            {t("l_e_a_p.into_limitless")}<br />
            {t("l_e_a_p.career_opportunities")} <span className="leap-hero__wave">{'👋'}</span>
          </h1>
          <p className="leap-hero__sub">
            {t("l_e_a_p.step_into_a_world_where_your_ideas")} <em>{t("l_e_a_p.innovators_problemsolvers_and_bold")}</em>
          </p>
          <div className="leap-hero__ctas">
            <a href="mailto:careers@locobuzz.com?subject=L.E.A.P. Internship Application" className="leap-btn-primary">
              {t("btn.apply")}
              <ArrowRight size={16} strokeWidth={2.4} />
            </a>
            <a href="#why" className="leap-btn-ghost">
              {t("l_e_a_p.see_why_interns_love_it")}
            </a>
          </div>
        </div>
      </section>

      {/* ── LEAP MEANING — 4 BIG LETTER CARDS ─────────────────────────── */}
      <section className="leap-letters" id="why">
        <div className="leap-section__head">
          <span className="leap-kicker">{t("l_e_a_p.what_leap_stands_for")}</span>
          <h2 className="leap-h2">{t("l_e_a_p.learn_explore_act_progress")}</h2>
          <p className="leap-sub">{t("l_e_a_p.four_words_one_playbook_for_the_bes")}</p>
        </div>
        <div className="leap-letters__grid">
          {LEAP_LETTERS.map(l => <article key={l.letter} className="leap-letter" style={{
          '--letterColor': l.color
        }}>
              <div className="leap-letter__big">{l.letter}</div>
              <div className="leap-letter__emoji" aria-hidden="true">{l.emoji}</div>
              <h3 className="leap-letter__word">{l.word}</h3>
              <p className="leap-letter__desc">{l.desc}</p>
            </article>)}
        </div>
      </section>

      {/* ── WHAT YOU GET — 4 PERK CARDS ──────────────────────────────── */}
      <section className="leap-perks">
        <div className="leap-section__head">
          <span className="leap-kicker">{t("l_e_a_p.what_youll_get")}</span>
          <h2 className="leap-h2">{t("l_e_a_p.real_work_real_wins_real_fast")}</h2>
        </div>
        <div className="leap-perks__grid">
          {PERKS.map(({
          Icon,
          title,
          desc,
          bg
        }) => <article key={title} className="leap-perk" style={{
          background: bg
        }}>
              <div className="leap-perk__icon"><Icon size={26} strokeWidth={2.2} color="#fff" /></div>
              <h3 className="leap-perk__title">{title}</h3>
              <p className="leap-perk__desc">{desc}</p>
            </article>)}
        </div>
      </section>

      {/* ── VIBE STRIP — fun office perks as emoji chips ─────────────── */}
      <section className="leap-vibe">
        <div className="leap-section__head">
          <span className="leap-kicker">{t("l_e_a_p.the_vibe")}</span>
          <h2 className="leap-h2">
            {t("l_e_a_p.because_ordinary_is_boring_and_your")} <span className="leap-h2__accent">{t("l_e_a_p.extraordinary")}</span>
          </h2>
        </div>
        <div className="leap-vibe__chips">
          {VIBE_BADGES.map(v => <span key={v.label} className="leap-vibe__chip">
              <span className="leap-vibe__emoji" aria-hidden="true">{v.emoji}</span>
              {v.label}
            </span>)}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="leap-cta">
        <div className="leap-cta__inner">
          <Lightbulb size={42} strokeWidth={1.6} color="#fff" />
          <h2 className="leap-cta__h">{t("l_e_a_p.ready_to_leap")}</h2>
          <p className="leap-cta__p">
            {t("l_e_a_p.send_your_cv_and_a_fun_fact_about_y")}{' '}
            <a href="mailto:careers@locobuzz.com" className="leap-cta__inlineLink">{t("l_e_a_p.careerslocobuzzcom")}</a>{' '}
            {t("l_e_a_p.bonus_points_if_you_tell_us_which_l")}
          </p>
          <a href="mailto:careers@locobuzz.com?subject=L.E.A.P. Internship Application" className="leap-btn-glow">
            <Zap size={16} strokeWidth={2.4} />
            {t("l_e_a_p.apply_now")}
            <ArrowRight size={16} strokeWidth={2.4} />
          </a>
        </div>
      </section>

      <InlineStyle css={`
        :root { --leap-pink: #FF6B9D; --leap-blue: #4F6EF7; --leap-green: #10B981; --leap-orange: #F5A623; --leap-violet: #6C47F5; }

        .leap-hero {
          position: relative;
          overflow: hidden;
          padding: 140px 24px 100px;
          text-align: center;
          background: linear-gradient(135deg, #FFE9F2 0%, #E9EEFE 35%, #E6F8F0 65%, #FFF4DE 100%);
          font-family: var(--font-inter), system-ui, sans-serif;
        }
        .leap-hero__blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.55;
          animation: leapFloat 18s ease-in-out infinite;
        }
        .leap-hero__blob--1 { width: 420px; height: 420px; top: -120px; left: -80px; background: var(--leap-pink); }
        .leap-hero__blob--2 { width: 360px; height: 360px; top: 100px; right: -60px; background: var(--leap-blue); animation-delay: -6s; }
        .leap-hero__blob--3 { width: 320px; height: 320px; bottom: -120px; left: 30%; background: var(--leap-green); animation-delay: -12s; }
        @keyframes leapFloat {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(40px,-30px) scale(1.08); }
        }
        .leap-sparkle {
          position: absolute;
          font-size: 24px;
          animation: leapTwinkle 3s ease-in-out infinite;
          pointer-events: none;
          user-select: none;
        }
        @keyframes leapTwinkle {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.2); }
        }

        .leap-hero__inner { position: relative; z-index: 1; max-width: 880px; margin: 0 auto; }
        .leap-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 16px;
          background: rgba(255,255,255,0.7);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(79,110,247,0.25);
          border-radius: 100px;
          font-size: 12.5px; font-weight: 700;
          color: var(--leap-blue);
          letter-spacing: 0.04em;
          margin-bottom: 24px;
        }
        .leap-hero__h1 {
          font-size: clamp(36px, 7vw, 76px);
          font-weight: 900;
          color: #0D1117;
          letter-spacing: -0.035em;
          line-height: 1.05;
          margin: 0 0 28px;
        }
        .leap-hero__accent {
          background: linear-gradient(135deg, var(--leap-pink), var(--leap-violet), var(--leap-blue));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .leap-hero__wave { display: inline-block; animation: leapWave 1.6s ease-in-out infinite; transform-origin: 70% 70%; }
        @keyframes leapWave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(20deg); }
          75% { transform: rotate(-15deg); }
        }
        .leap-hero__sub {
          font-size: 17px;
          color: #475569;
          line-height: 1.65;
          max-width: 640px;
          margin: 0 auto 36px;
        }
        .leap-hero__sub em { color: #0D1117; font-style: normal; font-weight: 700; }
        .leap-hero__ctas {
          display: flex; gap: 14px; justify-content: center; flex-wrap: wrap;
        }
        .leap-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 28px;
          border-radius: 100px;
          background: linear-gradient(135deg, var(--leap-pink), var(--leap-violet));
          color: #fff;
          font-size: 15px; font-weight: 700;
          text-decoration: none;
          box-shadow: 0 12px 32px rgba(255,107,157,0.45);
          transition: transform 0.18s, box-shadow 0.18s;
        }
        .leap-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 18px 40px rgba(255,107,157,0.55); }
        .leap-btn-ghost {
          display: inline-flex; align-items: center;
          padding: 14px 24px;
          border-radius: 100px;
          background: rgba(255,255,255,0.7);
          border: 1px solid rgba(15,23,42,0.10);
          color: #0D1117;
          font-size: 14px; font-weight: 600;
          text-decoration: none;
          transition: background 0.18s;
        }
        .leap-btn-ghost:hover { background: rgba(255,255,255,0.95); }

        /* ── Section heads ─────────────────────────────────────────── */
        .leap-section__head { text-align: center; max-width: 720px; margin: 0 auto 48px; }
        .leap-kicker {
          display: inline-block;
          font-size: 12px; font-weight: 800;
          color: var(--leap-violet);
          text-transform: uppercase;
          letter-spacing: 0.16em;
          margin-bottom: 14px;
        }
        .leap-h2 {
          font-size: clamp(28px, 4.4vw, 44px);
          font-weight: 900;
          color: #0D1117;
          letter-spacing: -0.025em;
          line-height: 1.1;
          margin: 0 0 12px;
        }
        .leap-h2__accent { background: linear-gradient(135deg, var(--leap-pink), var(--leap-orange)); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
        .leap-sub { font-size: 16px; color: #475569; line-height: 1.65; margin: 0; }

        /* ── Letter cards ──────────────────────────────────────────── */
        .leap-letters { padding: 100px 24px; background: #FFFFFF; }
        .leap-letters__grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .leap-letter {
          position: relative;
          padding: 32px 26px 28px;
          background: #FFFFFF;
          border: 1.5px solid #E8ECF4;
          border-radius: 24px;
          overflow: hidden;
          transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
        }
        .leap-letter::before {
          content: '';
          position: absolute;
          inset: -40% -20% auto auto;
          width: 70%;
          aspect-ratio: 1;
          background: radial-gradient(circle at center, var(--letterColor), transparent 60%);
          opacity: 0.18;
          pointer-events: none;
        }
        .leap-letter:hover {
          transform: translateY(-6px) rotate(-1deg);
          border-color: var(--letterColor);
          box-shadow: 0 24px 48px -16px var(--letterColor);
        }
        .leap-letter__big {
          font-family: var(--font-inter-tight), system-ui, sans-serif;
          font-size: 96px;
          font-weight: 900;
          line-height: 0.8;
          letter-spacing: -0.04em;
          color: var(--letterColor);
          margin-bottom: 8px;
        }
        .leap-letter__emoji { font-size: 32px; margin-bottom: 14px; }
        .leap-letter__word { font-size: 22px; font-weight: 800; color: #0D1117; letter-spacing: -0.02em; margin: 0 0 10px; }
        .leap-letter__desc { font-size: 13.5px; color: #4B5563; line-height: 1.6; margin: 0; }

        /* ── Perk cards ──────────────────────────────────────────── */
        .leap-perks { padding: 100px 24px; background: #F8FAFF; }
        .leap-perks__grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .leap-perk {
          padding: 28px 24px;
          border-radius: 22px;
          color: #fff;
          min-height: 200px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-shadow: 0 16px 40px -16px rgba(15,23,42,0.25);
          transition: transform 0.25s;
        }
        .leap-perk:hover { transform: translateY(-4px) rotate(1deg); }
        .leap-perk__icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: rgba(255,255,255,0.18);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
        }
        .leap-perk__title { font-size: 18px; font-weight: 800; color: #fff; letter-spacing: -0.015em; margin: 0; line-height: 1.25; }
        .leap-perk__desc { font-size: 14px; color: rgba(255,255,255,0.92); line-height: 1.55; margin: 0; }

        /* ── Vibe chips ─────────────────────────────────────────── */
        .leap-vibe { padding: 100px 24px; background: #FFFFFF; }
        .leap-vibe__chips {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 12px;
          max-width: 920px;
          margin: 0 auto;
        }
        .leap-vibe__chip {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #FEF3F2, #EEF2FF);
          border: 1.5px solid rgba(79,110,247,0.18);
          border-radius: 100px;
          font-size: 14px; font-weight: 600;
          color: #0D1117;
          transition: transform 0.18s, border-color 0.18s;
        }
        .leap-vibe__chip:hover { transform: scale(1.06) rotate(-2deg); border-color: var(--leap-pink); }
        .leap-vibe__emoji { font-size: 18px; }

        /* ── CTA ────────────────────────────────────────────────── */
        .leap-cta {
          padding: 100px 24px;
          background:
            radial-gradient(ellipse 70% 70% at 20% 30%, rgba(255,107,157,0.45) 0%, transparent 60%),
            radial-gradient(ellipse 70% 70% at 80% 70%, rgba(79,110,247,0.5) 0%, transparent 60%),
            linear-gradient(135deg, #0A1454, #02061F);
        }
        .leap-cta__inner {
          max-width: 720px; margin: 0 auto;
          text-align: center;
          color: #fff;
        }
        .leap-cta__h { font-size: clamp(32px, 5vw, 52px); font-weight: 900; color: #fff; letter-spacing: -0.025em; line-height: 1.1; margin: 18px 0 16px; }
        .leap-cta__p { font-size: 16px; color: rgba(255,255,255,0.85); line-height: 1.7; margin: 0 0 28px; }
        .leap-cta__inlineLink { color: #FFD479; font-weight: 700; }
        .leap-btn-glow {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 16px 32px;
          background: linear-gradient(135deg, #FFD479, #FF6B9D);
          color: #0A0E2E;
          font-size: 15px; font-weight: 800;
          text-decoration: none;
          border-radius: 100px;
          box-shadow: 0 14px 40px rgba(255,180,121,0.45);
          transition: transform 0.18s, box-shadow 0.18s;
        }
        .leap-btn-glow:hover { transform: translateY(-2px) scale(1.03); box-shadow: 0 20px 50px rgba(255,180,121,0.6); }

        /* ── Responsive ─────────────────────────────────────────── */
        @media (max-width: 980px) {
          .leap-letters__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .leap-perks__grid   { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 560px) {
          .leap-hero { padding: 100px 18px 72px; }
          .leap-hero__h1 { font-size: clamp(32px, 9vw, 48px); }
          .leap-letters, .leap-perks, .leap-vibe, .leap-cta { padding: 72px 18px; }
          .leap-letters__grid,
          .leap-perks__grid { grid-template-columns: 1fr; }
          .leap-letter__big { font-size: 78px; }
          .leap-hero__sub { font-size: 15px; }
          .leap-section__head { margin-bottom: 36px; }
        }
      `} />
    </>;
}